import React, { useCallback, useEffect, useState } from 'react';
import { documentsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';
import { useAuth } from '../auth/AuthContext';
import { authorColorVar } from './RedlineView';
import MentionInput, { displayName } from './MentionInput';
import { recordRight } from '../auth/recordRights';

/**
 * Threaded comments anchored to a passage of a document or revision.
 *
 * Distinct from the approve/reject `comment` field on a WorkflowStepAction --
 * that says something about a GATE; this says something about a PASSAGE, and
 * is writable by an approver who may not be able to edit the document at all
 * (services/comment_service.py::may_comment). Anchoring is by quoted text, not
 * an offset, so a comment whose passage has since been edited away is shown
 * DETACHED rather than silently re-pointed at whatever now sits there.
 *
 * `pendingAnchor`/`onAnchorConsumed` let a parent (RedlineView's selection
 * handler, wired up by the page that mounts both) hand this panel a quote to
 * pre-fill the composer with, so "select text, leave a comment" is one flow
 * rather than copy-paste.
 *
 * @MENTIONS (issue #75): the composer and every reply box are MentionInput,
 * and the mentionable list is fetched ONCE here rather than per composer. A
 * mention is sent as an id, never parsed out of the text -- see MentionInput's
 * own header. Rendering highlights the "@Name" runs that match the comment's
 * own mention snapshots, as React text nodes; nothing in a comment body has
 * ever been rendered as markup and this does not change that.
 *
 * `canComment` GATES THE COMPOSER AND THE REPLY BOXES -- not Resolve, see
 * below -- and since issue #210 its callers pass a real per-record answer
 * rather than leaving the `true` default. It comes from `DocumentDetailResponse
 * .can_comment`, computed server-side by the very
 * `services/comment_service.py::may_comment` the POST enforces -- so the two
 * cannot disagree.
 *
 * The default stays `true`, and that is load-bearing rather than legacy. Until
 * #210 this was shown unconditionally on purpose: whether an approver may
 * comment depends on active ApprovalGroupMember rows on the document's open
 * gate, which this component has no cheap way to re-derive, and CLAUDE.md's
 * rule for `can()` is to hide a button only when it would CERTAINLY fail.
 * `recordRight(doc?.can_comment, true)` at every call site preserves exactly
 * that behaviour whenever the flag is absent -- a fetch still in flight, or a
 * response built without an identity. A literal `false` is a real answer.
 *
 * RESOLVE/REOPEN HAS ITS OWN RIGHT, `comment.can_resolve`, and must not be
 * folded back into `canComment`. `may_resolve` is "whoever raised this point,
 * or an editor", which is neither a subset nor a superset of `may_comment`:
 * an approver on an open gate may reply to a thread somebody else started and
 * may not settle it, and -- the case that matters -- the AUTHOR of a comment
 * may still settle it long after their gate has closed and they can no longer
 * post at all. Sharing one prop got that second case backwards, hiding a
 * control the server honours.
 *
 * STILL AN AFFORDANCE. The backend enforces the real rule on every write.
 */
/**
 * The picked mentions whose name is still actually written in the text.
 *
 * Picking somebody and then deleting the name back out of the sentence should
 * not notify them -- the id is authoritative for WHO, but the text is what the
 * author can see, and notifying someone whose name is nowhere on screen would
 * be indistinguishable from a bug. The reverse (typing "@Jane Doe" by hand
 * without using the picker) deliberately does NOT create a mention: prose is
 * never parsed into ids.
 */
export function mentionsStillInText(ids, people, text) {
  const body = text || '';
  return ids.filter((id) => {
    const person = people.find((p) => p.id === id);
    return person && body.includes(`@${displayName(person)}`);
  });
}

/**
 * A comment body with its "@Name" runs highlighted, as React nodes.
 *
 * Never dangerouslySetInnerHTML and never marked/DOMPurify: a comment body has
 * always been a plain text node, and the <ins>/<del> surface stays quarantined
 * to RedlineView. Highlighting is driven by the comment's own mention snapshots
 * rather than by a regex over "@\w+", so a plain email address or an "@" typed
 * in passing is left alone.
 */
export function renderBody(body, mentions) {
  const names = (mentions || []).map((m) => m.name).filter(Boolean);
  if (names.length === 0) return body;
  // Longest first, so "@Jane Doe-Smith" is not half-matched by "@Jane Doe".
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const parts = [];
  let rest = body || '';
  let key = 0;
  while (rest.length > 0) {
    let bestAt = -1;
    let bestName = null;
    for (let i = 0; i < sorted.length; i += 1) {
      const at = rest.indexOf(`@${sorted[i]}`);
      if (at !== -1 && (bestAt === -1 || at < bestAt)) {
        bestAt = at;
        bestName = sorted[i];
      }
    }
    if (bestAt === -1) { parts.push(rest); break; }
    if (bestAt > 0) parts.push(rest.slice(0, bestAt));
    parts.push(
      <span className="comment-mention" key={`m${key += 1}`}>@{bestName}</span>,
    );
    rest = rest.slice(bestAt + bestName.length + 1);
  }
  return parts;
}

function CommentsPanel({ documentId, revisionId, canComment = true, pendingAnchor, onAnchorConsumed }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeResolved, setIncludeResolved] = useState(true);
  const [draft, setDraft] = useState('');
  const [draftAnchor, setDraftAnchor] = useState(null);
  const [replyDraft, setReplyDraft] = useState({}); // { [parentId]: text }
  const [submitting, setSubmitting] = useState(false);
  // Who may be @mentioned here, and who the author has actually picked.
  // draftMentions/replyMentions hold ids, which is what the API takes.
  const [people, setPeople] = useState([]);
  const [draftMentions, setDraftMentions] = useState([]);
  const [replyMentions, setReplyMentions] = useState({}); // { [parentId]: [ids] }

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await documentsAPI.listComments(documentId, revisionId, includeResolved);
      setComments(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [documentId, revisionId, includeResolved]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  useEffect(() => {
    // Non-fatal: if this fails the @ picker simply offers nobody, and the
    // comment box still works. Failing the whole panel over an autocomplete
    // would be worse than the missing affordance.
    let cancelled = false;
    documentsAPI.mentionable(documentId)
      .then((res) => { if (!cancelled) setPeople(res.data); })
      .catch((err) => console.error('Error loading mentionable users:', err));
    return () => { cancelled = true; };
  }, [documentId]);

  useEffect(() => {
    if (pendingAnchor) {
      setDraftAnchor(pendingAnchor);
      if (onAnchorConsumed) onAnchorConsumed();
    }
  }, [pendingAnchor, onAnchorConsumed]);

  const handlePost = async () => {
    if (!draft.trim()) return;
    setSubmitting(true);
    try {
      await documentsAPI.createComment(documentId, {
        body: draft,
        anchor_quote: draftAnchor || null,
        document_revision_id: revisionId || null,
        // Only the people still named in the text. Picking somebody and then
        // deleting the name back out should not notify them.
        mentioned_user_ids: mentionsStillInText(draftMentions, people, draft),
      });
      setDraft('');
      setDraftAnchor(null);
      setDraftMentions([]);
      await fetchComments();
    } catch (err) {
      console.error('Error posting comment:', err);
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId) => {
    const text = (replyDraft[parentId] || '').trim();
    if (!text) return;
    try {
      await documentsAPI.createComment(documentId, {
        body: text, parent_comment_id: parentId, document_revision_id: revisionId || null,
        mentioned_user_ids: mentionsStillInText(
          replyMentions[parentId] || [], people, text,
        ),
      });
      setReplyDraft((prev) => ({ ...prev, [parentId]: '' }));
      setReplyMentions((prev) => ({ ...prev, [parentId]: [] }));
      await fetchComments();
    } catch (err) {
      console.error('Error posting reply:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleResolve = async (comment) => {
    try {
      await documentsAPI.resolveComment(documentId, comment.id, !!comment.resolved_at);
      await fetchComments();
    } catch (err) {
      console.error('Error resolving comment:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleDelete = async (comment) => {
    if (!window.confirm('Withdraw this comment?')) return;
    try {
      await documentsAPI.deleteComment(documentId, comment.id);
      await fetchComments();
    } catch (err) {
      console.error('Error withdrawing comment:', err);
      setError(extractErrorMessage(err));
    }
  };

  if (loading) return <div className="loading">Loading comments...</div>;

  const unresolvedCount = comments.filter((c) => !c.resolved_at).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0 }}>
          💬 Comments {unresolvedCount > 0 && (
            <span className="badge" style={{ background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}>
              {unresolvedCount} unresolved
            </span>
          )}
        </h3>
        <label style={{ fontSize: '12.5px', color: 'var(--muted)' }}>
          <input type="checkbox" checked={includeResolved}
                 onChange={(e) => setIncludeResolved(e.target.checked)} />
          {' '}Show resolved
        </label>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="comments-panel">
        {comments.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: '13.5px' }}>No comments yet.</div>
        )}
        {comments.map((comment) => (
          <CommentThread
            key={comment.id}
            comment={comment}
            canComment={canComment}
            currentUserId={user?.id}
            people={people}
            replyDraft={replyDraft[comment.id] || ''}
            onReplyChange={(v) => setReplyDraft((prev) => ({ ...prev, [comment.id]: v }))}
            onReplyMention={(person) => setReplyMentions((prev) => {
              const current = prev[comment.id] || [];
              if (current.includes(person.id)) return prev;
              return { ...prev, [comment.id]: [...current, person.id] };
            })}
            onReply={() => handleReply(comment.id)}
            onResolve={handleResolve}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {canComment && (
        <div style={{ marginTop: '14px', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
          {draftAnchor && (
            <div className="comment-anchor-quote">
              On: "{draftAnchor}"
              {' '}
              <button type="button" className="btn-link" onClick={() => setDraftAnchor(null)}>
                (remove)
              </button>
            </div>
          )}
          <div className="comment-composer">
            <MentionInput
              rows={2}
              placeholder="Leave a comment... type @ to mention someone"
              value={draft}
              onChange={setDraft}
              people={people}
              onMention={(person) => setDraftMentions((prev) => (
                prev.includes(person.id) ? prev : [...prev, person.id]
              ))}
            />
          </div>
          <button className="btn btn-primary btn-small" style={{ marginTop: '6px' }}
                  onClick={handlePost} disabled={submitting || !draft.trim()}>
            {submitting ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      )}
    </div>
  );
}

function CommentThread({ comment, canComment, currentUserId, people, replyDraft,
                        onReplyChange, onReplyMention, onReply, onResolve, onDelete }) {
  const colorVar = authorColorVar(String(comment.author_user_id));
  // Withdraw is strictly the author's -- unlike comment/resolve above, this we
  // CAN verify precisely client-side (author_user_id is right there on the
  // row), so there is no reason to show a control that would always 403.
  const isOwn = currentUserId != null && comment.author_user_id === currentUserId;
  return (
    <div className={`comment-thread${comment.resolved_at ? ' resolved' : ''}`}>
      {comment.anchor_quote && (
        <div className={`comment-anchor-quote${comment.anchor_found ? '' : ' detached'}`}>
          {comment.anchor_found ? 'On: ' : 'On (context no longer present): '}
          "{comment.anchor_quote}"
        </div>
      )}
      <div className="comment-body">
        <span className="comment-author-dot" style={{ '--redline-author-color': colorVar ? `var(${colorVar})` : undefined }} />
        <div style={{ flex: 1 }}>
          <div className="comment-meta">
            <strong style={{ color: 'var(--ink)' }}>{comment.author_name}</strong>
            {comment.impersonator_name && ` (via ${comment.impersonator_name})`}
            {' — '}{new Date(comment.created_at).toLocaleString()}
            {comment.resolved_at && (
              <span style={{ marginLeft: '8px', color: 'var(--success-ink)' }}>
                ✓ resolved by {comment.resolved_by_name}
              </span>
            )}
          </div>
          <div style={{ margin: '4px 0' }}>
            {renderBody(comment.body, comment.mentions)}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {/* Its OWN right, not canComment. may_resolve is "whoever raised
                this point, or an editor" -- so the author of a comment may
                still settle it long after their gate has closed and they can
                no longer post at all. Gating this on canComment got exactly
                that case backwards. Falls back to canComment when the server
                did not compute it, which is the pre-existing behaviour. */}
            {recordRight(comment.can_resolve, canComment) && (
              <button type="button" className="btn-link" onClick={() => onResolve(comment)}>
                {comment.resolved_at ? 'Reopen' : 'Resolve'}
              </button>
            )}
            {isOwn && (
              <button type="button" className="btn-link" onClick={() => onDelete(comment)}>
                Withdraw
              </button>
            )}
          </div>

          {comment.replies.map((reply) => (
            <div key={reply.id} className="comment-reply">
              <div className="comment-meta">
                <strong style={{ color: 'var(--ink)' }}>{reply.author_name}</strong>
                {reply.impersonator_name && ` (via ${reply.impersonator_name})`}
                {' — '}{new Date(reply.created_at).toLocaleString()}
              </div>
              <div style={{ margin: '4px 0' }}>
                {renderBody(reply.body, reply.mentions)}
              </div>
              {currentUserId != null && reply.author_user_id === currentUserId && (
                <button type="button" className="btn-link" onClick={() => onDelete(reply)}>
                  Withdraw
                </button>
              )}
            </div>
          ))}

          {canComment && (
            <div className="comment-composer" style={{ marginTop: '6px', display: 'flex', gap: '6px' }}>
              <MentionInput
                multiline={false}
                placeholder="Reply... type @ to mention someone"
                value={replyDraft}
                onChange={onReplyChange}
                people={people}
                onMention={onReplyMention}
              />
              <button className="btn btn-secondary btn-small" onClick={onReply} disabled={!replyDraft.trim()}>
                Reply
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommentsPanel;
